import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { services } from '../data/mockData';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';

const Services = () => {
  const { serviceId } = useParams();

  // If viewing a specific service
  if (serviceId) {
    const service = services.find(s => s.id === serviceId);
    
    if (!service) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-[#1e3a52] mb-4">Service Not Found</h1>
            <Link to="/services">
              <Button className="bg-[#5ba4c8] hover:bg-[#4a93b7]">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div>
        <SEO 
          title={`${service.title} - Professional Security Services in Miami, Florida`}
          description={`${service.fullDesc} ${service.title} services in Miami, Florida. ${service.features.join(', ')}. Contact Bu Security for a free quote.`}
          keywords={`${service.title.toLowerCase()}, ${service.title.toLowerCase()} Miami, ${service.title.toLowerCase()} Florida, security services Miami, professional security guards`}
          image={service.image}
        />
        {/* Hero Section */}
        <section className="relative h-[400px] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${service.image}')` }}
          />
          <div className="absolute inset-0 bg-[#1e3a52]/80" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 w-full">
            <div className="flex items-center gap-2 text-sm text-gray-300 mb-4">
              <Link to="/" className="hover:text-[#5ba4c8]">Home</Link>
              <ChevronRight size={16} />
              <Link to="/services" className="hover:text-[#5ba4c8]">Services</Link>
              <ChevronRight size={16} />
              <span className="text-[#5ba4c8]">{service.title}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-wider">
              {service.title}
            </h1>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-[#1e3a52] mb-6">About This Service</h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {service.fullDesc}
                </p>
                
                <h3 className="text-xl font-bold text-[#1e3a52] mb-4">Key Features</h3>
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-[#5ba4c8] rounded-full flex items-center justify-center">
                        <Check size={14} className="text-white" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link to="/quote">
                  <Button className="bg-[#5ba4c8] hover:bg-[#4a93b7] text-white px-8 py-3">
                    Request a Quote <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
              
              <div>
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-[400px] object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Other Services */}
        <section className="py-16 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-[#1e3a52] mb-12">Other Services</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {services.filter(s => s.id !== serviceId).slice(0, 3).map((s) => (
                <Link 
                  key={s.id} 
                  to={`/services/${s.id}`}
                  className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={s.image} 
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-[#1e3a52] mb-2">{s.title}</h3>
                    <p className="text-gray-600 text-sm">{s.shortDesc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Services overview page
  return (
    <div>
      <SEO 
        title="Security Services - Armed & Unarmed Guards, Mobile Patrol, Event Security"
        description="Comprehensive security services including armed guards, unarmed guards, mobile patrols, event security, employee termination services, and surveillance monitoring in Miami, Florida."
        keywords="security services Miami, armed security guards, unarmed guards, mobile patrol services, event security Miami, surveillance monitoring, employee termination security, security guard services Florida"
      />
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1618371690240-e0d46eead4b8?w=1920&h=800&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-[#1e3a52]/80" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 w-full">
          <div className="flex items-center gap-2 text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-[#5ba4c8]">Home</Link>
            <ChevronRight size={16} />
            <span className="text-[#5ba4c8]">Services</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-wider">
            Our Services
          </h1>
          <p className="text-gray-300 mt-4 max-w-2xl">
            Comprehensive security solutions tailored to meet your specific needs
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-[#5ba4c8]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link 
                key={service.id} 
                to={`/services/${service.id}`}
                className="group relative h-[400px] overflow-hidden rounded-lg"
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url('${service.image}')` }}
                />
                <div className="absolute inset-0 bg-[#1e3a52]/70 group-hover:bg-[#1e3a52]/80 transition-colors" />
                
                <div className="relative h-full flex flex-col justify-center items-center text-center p-6">
                  <h3 className="text-white text-xl font-bold uppercase tracking-wider mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 text-sm mb-6">
                    {service.shortDesc}
                  </p>
                  <div className="border-2 border-white text-white px-6 py-2 text-sm font-medium group-hover:bg-white group-hover:text-[#1e3a52] transition-all">
                    More Info
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1e3a52]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Need a Custom Security Solution?
          </h2>
          <p className="text-gray-300 mb-8">
            Contact us to discuss your specific security requirements
          </p>
          <Link to="/quote">
            <Button className="bg-[#5ba4c8] hover:bg-[#4a93b7] text-white px-8 py-3">
              Request a Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
